import { useEffect, useRef, useState } from 'react';

import { fadeIn, fadeOut, basicFadeOut, basicFadeIn } from './../animations/hotspotAnimations';
import isArrayEqual from './../utils/isArrayEqual';

// How long the "settle" escape hatch (see settleRef below) takes to bring
// mid-animation hotspots to rest before a queued click's own action runs.
const SETTLE_DURATION = 200;

const getAllHotspots = () => {
  return [...document.querySelectorAll('.mapComponent .hotspot')];
};

const getSetHotspots = () => {
  return [...document.querySelectorAll('.mapComponent .is-set')];
};

const getUnsetHotspots = () => {
  return [...document.querySelectorAll('.mapComponent .is-unset')];
};
const getInactive = (tagGroups) => {
  const inactiveArray = [];
  tagGroups.forEach((tagGroup) => {
    tagGroup.groupTags.data.forEach((tag) => {
      if (!tag.active) {
        inactiveArray.push(tag.id);
      }
    });
  });

  return inactiveArray;
};

const useHotspotFilter = (currentData, tagState, tutorialFlag, setIsAnimating, settleRef) => {
  const [inactiveTags, setInactiveTags] = useState([]);
  const [hotspotData, setHotspotData] = useState(currentData.currentMonth.hotspots.items);

  // Identifies the "current" fadeOut/fadeIn chain. Bumped whenever a new
  // transition starts, including by settleRef's forced settle below - every
  // async continuation of a chain checks this before touching state, so a
  // chain that's been superseded (settled, or a newer month/tag change) just
  // no-ops instead of applying stale data on top of whatever came after it.
  const transitionIdRef = useRef(0);

  //////////////////////////////////////////////////////
  //
  // on first open
  //
  //////////////////////////////////////////////////////
  useEffect(() => {
    setIsAnimating(true);
    setTimeout(() => {
      if (!tutorialFlag) {
        const hotspots = getSetHotspots();
        fadeIn(hotspots, () => setIsAnimating(false));
      }
    });
  }, [tutorialFlag]);

  //////////////////////////////////////////////////////
  //
  // if data change update
  //
  //////////////////////////////////////////////////////
  useEffect(() => {
    // if data is stale animate them out!
    if (!isArrayEqual(currentData.currentMonth.hotspots.items, hotspotData)) {
      const myId = ++transitionIdRef.current;
      setTimeout(() => {
        if (tutorialFlag || transitionIdRef.current !== myId) return;
        const hotspots = getAllHotspots();
        setIsAnimating(true);
        fadeOut(hotspots, () => {
          if (transitionIdRef.current !== myId) return;
          setHotspotData(currentData.currentMonth.hotspots.items);
          // Wait for the just-triggered setHotspotData to actually commit/paint
          // before querying the DOM - a bare setTimeout(0) here routinely fires
          // before React has re-rendered, so fadeIn would target the previous
          // month's (already-hidden) hotspots while the new ones - freshly
          // mounted with no fade-in ever applied - just snap in unanimated.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (transitionIdRef.current !== myId) return;
              const setSpots = getSetHotspots();
              fadeIn(setSpots, () => {
                if (transitionIdRef.current !== myId) return;
                setIsAnimating(false);
              });
            });
          });
        });
      }, 0);
    }
  }, [currentData, hotspotData]);

  //////////////////////////////////////////////////////
  //
  // settle escape hatch - lets a click that lands while isAnimating is
  // true skip waiting out whatever's currently running. Registers itself
  // on settleRef (a ref living on AnimationContext) so sibling components
  // (TimelineView, TimelineCompact, YearSelect, FilterView) can call it
  // instead of silently dropping the click.
  //
  //////////////////////////////////////////////////////
  useEffect(() => {
    if (!settleRef) return;
    settleRef.current = (onSettled) => {
      // Supersede whatever fadeOut/fadeIn chain is in flight so its eventual
      // (now-stale) callbacks no-op instead of fighting what happens next.
      transitionIdRef.current += 1;
      basicFadeIn(getSetHotspots(), SETTLE_DURATION);
      basicFadeOut(getUnsetHotspots(), SETTLE_DURATION);
      setTimeout(() => {
        setIsAnimating(false);
        if (onSettled) onSettled();
      }, SETTLE_DURATION);
    };
    return () => {
      settleRef.current = null;
    };
  }, [settleRef, setIsAnimating]);

  //////////////////////////////////////////////////////
  //
  // if tags change update
  //
  //////////////////////////////////////////////////////
  useEffect(() => {
    setInactiveTags(getInactive(tagState));

    // Same reasoning as above: is-set/is-unset depends on the inactiveTags
    // state just set on the previous line, so the DOM query has to wait for
    // that render to actually commit, not just the next macrotask.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (tutorialFlag) return;
        const setSpots = getSetHotspots();
        const unsetSpots = getUnsetHotspots();
        basicFadeIn(setSpots);
        basicFadeOut(unsetSpots);
      });
    });
  }, [tagState]);

  return {
    inactiveTags,
    hotspotData,
  };
};

export default useHotspotFilter;
